import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum adaptiveAssessmentStateType {
  START = 'START',
  AWAITING_USER_START = 'AWAITING USER START',
  ATTEMPT = 'ATTEMPT',
  FINISHED = 'FINISHED',
}

export enum attemptQuestionType {
  CODING = 'Coding',
  OBJECTIVE = 'Objective',
}

export enum questionStateType {
  CONFIRMATION_PENDING = 'CONFIRMATION PENDING',
  SUBMISSION_PENDING = 'SUBMISSION PENDING',
  CLEAN_STATE = 'CLEAN STATE',
}

export enum chatMessageOwner {
  USER = 'USER',
  BOT = 'BOT',
}

export enum messageStringType {
  STRING = 'STRING',
  OPTIONS = 'OPTIONS',
  DESCRIPTION = 'DESCRIPTION',
}

export interface messageType {
  owner: chatMessageOwner;
  type: messageStringType;
  message: string;
}

export interface userDetailsType {
  name: string;
  email: string;
}

export interface adaptiveAssessmentState {
  history: messageType[];
  current_question_number: number;
  total_question: number;
  total_time: number;
  time_remaining: number;
  assessment_name: string;
  company_name: string;
  question_answered: number;
  current_state: adaptiveAssessmentStateType;
  question_state: questionStateType;
  questionData: any;
  language: any;
  messages: messageType[];
  enableChat: boolean;
  assessmentStarted: boolean;
  currentQuestionType: attemptQuestionType;
  questionLoading: boolean;
  options: string[];
  showOptions: boolean;
  userDetails: userDetailsType;
  codeRunning: boolean;
  startMessages: string[];
}

const initialState: adaptiveAssessmentState = {
  history: [],
  current_question_number: 1,
  total_question: 0,
  total_time: 0,
  assessment_name: '',
  language: null,
  company_name: '',
  question_answered: 0,
  time_remaining: Infinity,
  current_state: adaptiveAssessmentStateType.START,
  question_state: questionStateType.CLEAN_STATE,
  questionData: null,
  messages: [],
  enableChat: false,
  assessmentStarted: false,
  currentQuestionType: attemptQuestionType.OBJECTIVE,
  questionLoading: false,
  showOptions: false,
  options: [],
  userDetails: {
    name: '',
    email: '',
  },
  codeRunning: false,
  startMessages: [],
};

export const adaptiveAssessmentSlice = createSlice({
  name: 'adaptiveAssessment',
  initialState,
  reducers: {
    setAdaptiveAssessmentInfo: (
      state,
      action: PayloadAction<adaptiveAssessmentState>
    ) => {
      const {
        total_question,
        total_time,
        assessment_name,
        company_name,
        question_answered,
      } = action.payload;
      state.total_question = total_question;
      state.total_time = total_time;
      state.assessment_name = assessment_name;
      state.company_name = company_name;
      state.question_answered = question_answered;
      state.current_question_number = question_answered + 1;
    },
    setCurrentState: (
      state,
      action: PayloadAction<adaptiveAssessmentStateType>
    ) => {
      state.current_state = action.payload;
    },
    setQuestionState: (state, action: PayloadAction<questionStateType>) => {
      state.question_state = action.payload;
    },
    setTimeRemaining: (state, action: PayloadAction<number>) => {
      state.time_remaining = action.payload;
    },
    setAdaptiveQuestionAnswered: (state, action: PayloadAction<number>) => {
      state.question_answered = action.payload;
      state.current_question_number = action.payload + 1;
    },
    setCurrentQuestion: (state, action: PayloadAction<number>) => {
      state.current_question_number = action.payload;
    },
    setQuestionData: (state, action: PayloadAction<any>) => {
      state.questionData = action.payload;
    },
    resetQuestionData: (state) => {
      state.questionData = null;
    },
    pushMessage: (state, action: PayloadAction<messageType>) => {
      state.messages = [...state.messages, action.payload];
    },
    resetMessage: (state) => {
      state.messages = [];
    },
    setChatEnabled: (state, action: PayloadAction<boolean>) => {
      state.enableChat = action.payload;
    },
    startAssessment: (state) => {
      state.assessmentStarted = true;
    },
    stopAssessment: (state) => {
      state.assessmentStarted = false;
    },
    setQuestionType: (state, action: PayloadAction<attemptQuestionType>) => {
      state.currentQuestionType = action.payload;
    },
    setQuestionloading: (state, action: PayloadAction<boolean>) => {
      state.questionLoading = action.payload;
    },
    setOptions: (state, action: PayloadAction<string[]>) => {
      state.options = action.payload;
    },
    setShowOptions: (state, action: PayloadAction<boolean>) => {
      state.showOptions = action.payload;
    },
    setUserDetails: (state, action: PayloadAction<userDetailsType>) => {
      state.userDetails = action.payload;
    },
    setLanguage: (state, action: PayloadAction<any>) => {
      state.language = action.payload;
    },
    setAdaptiveCodeRunning: (state, action: PayloadAction<boolean>) => {
      state.codeRunning = action.payload;
    },
    setAdaptiveTotalQuestion: (state, action: PayloadAction<number>) => {
      state.total_question = action.payload;
    },
    setStartMessages: (state, action: PayloadAction<string[]>) => {
      state.startMessages = action.payload;
    },
    pushToHistory: (state, action: PayloadAction<messageType>) => {
      state.history = [...state.history, action.payload];
    },
  },
});

export const {
  setAdaptiveAssessmentInfo,
  setCurrentState,
  setQuestionState,
  setTimeRemaining,
  setAdaptiveQuestionAnswered,
  pushMessage,
  resetMessage,
  resetQuestionData,
  setQuestionData,
  startAssessment,
  stopAssessment,
  setQuestionloading,
  setQuestionType,
  setOptions,
  setShowOptions,
  setUserDetails,
  setLanguage,
  setAdaptiveCodeRunning,
  setAdaptiveTotalQuestion,
  setStartMessages,
  pushToHistory,
} = adaptiveAssessmentSlice.actions;

export default adaptiveAssessmentSlice.reducer;
